import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { useIsLoggedIn } from '@zoopla/user';
import { ConsentModal } from '@zoopla/consent-modal';
import {
    SpecifyRelationshipModal,
    useSaveRelationship
} from '@zoopla/property-details/track-property';
import type {
    Property,
    TrackedProperty,
    TrackedPropertyRelationship
} from './graphql.generated';
import { useTrackProperty } from './useTrackProperty';
import type { Status } from './useTrackProperty';
import { logError } from '@zoopla/logger';
import { AuthModal } from '@zoopla/authentication/modal';
import { HousePricesTrackedPropertiesDocument } from './queries/housePricesTrackedProperties.query.generated';
import type {
    HousePricesTrackedPropertiesQuery,
    HousePricesTrackedPropertiesQueryVariables
} from './queries/housePricesTrackedProperties.query.generated';
import HOUSE_PRICES_TRACKED_PROPERTIES_QUERY from './queries/housePricesTrackedProperties.query';
import { getSearchPropertiesQueryVariables } from './helpers';
import { useLazyQuery } from '@apollo/client';
import { getGeoIdentifier } from './helpers/getSearchPropertiesQueryVariables';
import { triggerTracking } from '@zoopla/tracking';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';

type States = Record<string, Status>;
export interface ChildProps {
    trackClick: (p: Property, extraTaxonomy?: Record<string, unknown>) => void;
    states: States;
}

function TrackPropertyWrapper({
    children,
    properties,
    setPropertyBeingTracked,
    propertyBeingTracked
}: {
    children: (cp: ChildProps) => ReactElement[];
    properties: Property[];
    setPropertyBeingTracked: (Property: Property | undefined) => void;
    propertyBeingTracked: Property | undefined;
}) {
    const isLoggedIn = useIsLoggedIn();

    const [isRelationshipModalShowing, setIsRelationshipModalShowing] =
        useState(false);

    const [isAuthModalShowing, openAuthModal, dismissAuthModal] =
        useAuthModal();

    const {
        dismissRelationshipModal,
        saveAndCloseRelationshipModal,
        saveRelationshipLoading,
        saveRelationshipError
    } = useSaveRelationship({ uprn: propertyBeingTracked?.uprn }, () =>
        setIsRelationshipModalShowing(false)
    );

    const mutableStates = useRef<States>({});
    const [, rerender] = useState({});
    const [isConsentModalShowing, setIsConsentModalShowing] = useState(false);
    const [pendingProperty, setPendingProperty] = useState<Property | null>(
        null
    );

    const { query } = useRouter();

    const geoIdentifier = getGeoIdentifier(query['search-path']);
    const trackedPropertyQueryVariables = getSearchPropertiesQueryVariables(
        query,
        geoIdentifier
    );
    const { trackOrUntrackProperty } = useTrackProperty();

    const [getTrackedPropertyData, { data }] = useLazyQuery<
        HousePricesTrackedPropertiesQuery,
        HousePricesTrackedPropertiesQueryVariables
    >(HousePricesTrackedPropertiesDocument, {
        query: HOUSE_PRICES_TRACKED_PROPERTIES_QUERY,
        variables: trackedPropertyQueryVariables,
        errorPolicy: 'ignore'
    });

    const updatePendingLocalStates = (p: Property, result: Status) => {
        if (result !== 'pending' && p.trackedProperty) {
            const trackedProperty = p.trackedProperty as TrackedProperty;
            trackedProperty.isTracked = result === 'tracked';
        }

        mutableStates.current = {
            ...mutableStates.current,
            [p?.uprn ?? '']: result
        };

        rerender({});
    };

    const trackClick = async (
        p: Property,
        extraTaxonomy?: Record<string, unknown>
    ) => {
        try {
            setPendingProperty(p);

            if (!isLoggedIn) {
                openAuthModal(null);
            } else {
                if (!p.trackedProperty?.isTracked) {
                    setIsConsentModalShowing(true);
                }
                updatePendingLocalStates(p, 'pending');
                trackOrUntrackProperty(
                    p,
                    undefined,
                    undefined,
                    extraTaxonomy
                ).then((result) => {
                    updatePendingLocalStates(p, result);
                });
            }
        } catch (err) {
            logError('House prices track property error: ', err);
        }
    };

    useEffect(() => {
        if (
            data?.propertiesSearch.__typename === 'PropertiesSearchConnection'
        ) {
            data?.propertiesSearch.edges?.forEach((edge) => {
                const status = edge?.node?.trackedProperty?.isTracked
                    ? 'tracked'
                    : 'untracked';
                mutableStates.current[edge?.node?.uprn ?? ''] = status;

                const property = properties.find(
                    (p) => p.uprn === edge?.node?.uprn
                );
                updatePendingLocalStates(property as Property, status);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(
        () => propertyBeingTracked && setIsRelationshipModalShowing(true),
        [propertyBeingTracked]
    );

    const updatePropertyBeingTracked = () => {
        if (pendingProperty?.trackedProperty?.isTracked) {
            setPropertyBeingTracked(pendingProperty);
        }
    };

    const consentClickHandler = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'property details',
            eventLabel: 'alert on',
            eventValue: pendingProperty?.uprn,
            eventAction: 'click'
        });

        updatePropertyBeingTracked();
    };

    const dismissModalHandler = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'property details',
            eventLabel: 'alert off',
            eventValue: pendingProperty?.uprn,
            eventAction: 'click'
        });

        updatePropertyBeingTracked();
    };

    return (
        <>
            <SpecifyRelationshipModal
                action={{
                    dismissRelationshipModal: () => {
                        dismissRelationshipModal();
                        setPropertyBeingTracked(undefined);
                    },
                    saveAndCloseRelationshipModal: async (
                        relationship: TrackedPropertyRelationship
                    ) => {
                        await saveAndCloseRelationshipModal(relationship);
                        setPropertyBeingTracked(undefined);
                    },
                    formShownEventLabel: 'save property',
                    formShownEventValue: pendingProperty?.uprn
                        ? parseInt(pendingProperty?.uprn, 10)
                        : 0
                }}
                isShowing={isRelationshipModalShowing}
                isSubmissionLoading={saveRelationshipLoading}
                submissionError={saveRelationshipError}
            />
            <ConsentModal
                closeModal={() => setIsConsentModalShowing(false)}
                isOpen={isConsentModalShowing}
                updateStatus={updatePendingLocalStates}
                property={pendingProperty}
                hostPage="House prices"
                relationship="NONE"
                handleConsentClick={consentClickHandler}
                handleDismissClick={dismissModalHandler}
            />
            <AuthModal
                isShowing={isAuthModalShowing}
                close={dismissAuthModal}
                actionAfterAuth={{
                    action: () => {
                        if (!pendingProperty) {
                            return;
                        }

                        const pendingPropertyIsTrackedRemotely =
                            pendingProperty?.trackedProperty?.isTracked;
                        const pendingPropertyIsTrackedLocally =
                            pendingProperty?.uprn
                                ? !!mutableStates.current?.[
                                      pendingProperty?.uprn
                                  ]
                                : false;
                        const pendingPropertyIsTracked =
                            pendingPropertyIsTrackedLocally ??
                            pendingPropertyIsTrackedRemotely;

                        updatePendingLocalStates(pendingProperty, 'pending');
                        trackOrUntrackProperty(pendingProperty)
                            .then((result) => {
                                updatePendingLocalStates(
                                    pendingProperty,
                                    result
                                );

                                properties.forEach((p) =>
                                    updatePendingLocalStates(p, 'pending')
                                );
                                getTrackedPropertyData();
                            })
                            .catch((err) => {
                                logError(
                                    `Error fetching tracked properties after auth: ${JSON.stringify(
                                        err
                                    )}`
                                );
                            });

                        if (!pendingPropertyIsTracked) {
                            setIsConsentModalShowing(true);
                        }
                    },
                    eventLabel: 'save_property',
                    segment: {
                        page: '/house-prices/',
                        section: 'house-prices',
                        activity: 'property_details'
                    }
                }}
                heading="Track properties"
            />
            {children({
                trackClick,
                states: mutableStates.current
            })}
        </>
    );
}

export default TrackPropertyWrapper;
